import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/AdminLayout';
import { LoadingState, ConfirmModal, DataTable } from '../../../components/admin/shared';
import { Plus, Edit2, Trash2, Save, X, CheckCircle, Eye, Code } from 'lucide-react';
import educentialApi from '../../../api/educentialApi';

const EmailTemplatesConfig = () => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  const [success, setSuccess] = useState(false);

  const categories = [
    { value: 'welcome', label: 'Welcome' },
    { value: 'status_update', label: 'Status Update' },
    { value: 'reminder', label: 'Reminder' },
    { value: 'notification', label: 'Notification' },
    { value: 'password_reset', label: 'Password Reset' },
    { value: 'verification', label: 'Verification' }
  ];

  const commonVariables = [
    { key: '{{userName}}', description: 'User\'s full name' },
    { key: '{{userEmail}}', description: 'User\'s email address' },
    { key: '{{applicationId}}', description: 'Application ID' },
    { key: '{{status}}', description: 'Application status' },
    { key: '{{serviceName}}', description: 'Service name' },
    { key: '{{link}}', description: 'Action link/URL' },
    { key: '{{date}}', description: 'Current date' },
    { key: '{{platformName}}', description: 'Platform name (Educential)' }
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await educentialApi.get('/api/admin/settings/email-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading email templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingTemplate({
      id: null,
      name: '',
      category: 'notification',
      subject: '',
      body: '',
      variables: [],
      active: true
    });
  };

  const handleEdit = (template) => {
    setEditingTemplate({ ...template });
  };

  const handleSave = async () => {
    try {
      if (editingTemplate.id) {
        // Update existing
        await educentialApi.patch(`/api/admin/settings/email-templates/${editingTemplate.id}`, editingTemplate);
      } else {
        // Create new
        await educentialApi.post('/api/admin/settings/email-templates', editingTemplate);
      }

      setEditingTemplate(null);
      setSuccess(true);
      loadTemplates();
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving email template:', error);
      alert('Failed to save email template. Please try again.');
    }
  };

  const handleDelete = async () => {
    try {
      await educentialApi.delete(`/api/admin/settings/email-templates/${showDeleteModal.id}`);
      setShowDeleteModal(null);
      setSuccess(true);
      loadTemplates();
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error deleting email template:', error);
      alert('Failed to delete email template. Please try again.');
    }
  };

  const insertVariable = (variable) => {
    if (!editingTemplate) return;

    const textarea = document.getElementById('template-body');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = editingTemplate.body;
    const before = text.substring(0, start);
    const after = text.substring(end);

    setEditingTemplate({
      ...editingTemplate,
      body: before + variable + after
    });

    // Set cursor position after inserted variable
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + variable.length;
      textarea.focus();
    }, 0);
  };

  const renderPreview = (template) => {
    let body = template.body;

    // Replace variables with sample data
    body = body.replace(/{{userName}}/g, 'John Doe');
    body = body.replace(/{{userEmail}}/g, 'john.doe@example.com');
    body = body.replace(/{{applicationId}}/g, '#12345');
    body = body.replace(/{{status}}/g, 'Under Review');
    body = body.replace(/{{serviceName}}/g, 'Overseas Education');
    body = body.replace(/{{link}}/g, 'https://educential.com/dashboard');
    body = body.replace(/{{date}}/g, new Date().toLocaleDateString());
    body = body.replace(/{{platformName}}/g, 'Educential');

    return body;
  };

  const columns = [
    {
      key: 'name',
      label: 'Template Name',
      render: (template) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{template.name}</div>
          <div className="text-xs text-gray-500">{template.category}</div>
        </div>
      )
    },
    {
      key: 'subject',
      label: 'Subject',
      render: (template) => <span className="text-sm text-gray-700">{template.subject}</span>
    },
    {
      key: 'active',
      label: 'Status',
      render: (template) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          template.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {template.active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'updatedAt',
      label: 'Last Updated',
      render: (template) => (
        <span className="text-sm text-gray-600">
          {new Date(template.updatedAt).toLocaleDateString()}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (template) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowPreview(template);
            }}
            className="p-1 text-gray-600 hover:bg-gray-50 rounded"
            title="Preview"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(template);
            }}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteModal(template);
            }}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  if (loading && !editingTemplate) {
    return (
      <AdminLayout title="Email Templates">
        <LoadingState message="Loading email templates..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Email Templates">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Email Templates</h2>
            <p className="text-gray-600 mt-1">Manage email templates for automated notifications</p>
          </div>
          <button
            onClick={handleAddNew}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            <span>Add Template</span>
          </button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-900 font-medium">Email template saved successfully!</p>
          </div>
        )}

        {/* Edit Form */}
        {editingTemplate && (
          <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-blue-500">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingTemplate.id ? 'Edit Email Template' : 'Create New Email Template'}
              </h3>
              <button
                onClick={() => setEditingTemplate(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Form */}
              <div className="lg:col-span-2 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Template Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editingTemplate.name}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                      placeholder="application_submitted"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={editingTemplate.category}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, category: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject Line <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editingTemplate.subject}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2"
                    placeholder="Your application has been submitted"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Body <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="template-body"
                    value={editingTemplate.body}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, body: e.target.value })}
                    rows={12}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 font-mono text-sm"
                    placeholder="Hi {{userName}},&#10;&#10;Your application #{{applicationId}} has been received.&#10;&#10;Thanks,&#10;{{platformName}} Team"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use variables from the sidebar to personalize your email
                  </p>
                </div>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editingTemplate.active}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, active: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Active (template can be used)</span>
                </label>
              </div>

              {/* Variables Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-gray-50 rounded-lg p-4 sticky top-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <Code className="w-4 h-4 text-gray-600" />
                    <h4 className="text-sm font-semibold text-gray-900">Available Variables</h4>
                  </div>
                  <div className="space-y-2">
                    {commonVariables.map((variable) => (
                      <button
                        key={variable.key}
                        onClick={() => insertVariable(variable.key)}
                        className="w-full text-left p-2 bg-white border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      >
                        <div className="font-mono text-xs text-blue-600">{variable.key}</div>
                        <div className="text-xs text-gray-600 mt-0.5">{variable.description}</div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Click a variable to insert it at cursor position
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
              <button
                onClick={() => setEditingTemplate(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowPreview(editingTemplate)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <Eye className="w-4 h-4" />
                <span>Preview</span>
              </button>
              <button
                onClick={handleSave}
                disabled={!editingTemplate.name || !editingTemplate.subject || !editingTemplate.body}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                <span>Save Template</span>
              </button>
            </div>
          </div>
        )}

        {/* Templates Table */}
        <DataTable
          columns={columns}
          data={templates}
          loading={loading}
          emptyMessage="No email templates found. Click 'Add Template' to create one."
        />

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Email Preview</h3>
                  <button
                    onClick={() => setShowPreview(null)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">SUBJECT</p>
                  <p className="text-sm font-medium text-gray-900">{showPreview.subject}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">BODY</p>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                      {renderPreview(showPreview)}
                    </pre>
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  This preview shows sample data for variables. Actual emails will use real user data.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteModal !== null}
          title="Delete Email Template?"
          message={`Are you sure you want to delete the template "${showDeleteModal?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          dangerous={true}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(null)}
        />
      </div>
    </AdminLayout>
  );
};

export default EmailTemplatesConfig;
